import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
  Textarea,
  Select,
  Tabs,
  TabItem,
  type TabsRef,
} from "flowbite-react";
import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "react-toastify";
import { updateVideo } from "../../services/videos";
import { searchEquipment, getAllEquipment } from "../../services/exercises";
import type { Equipment } from "../../types/apiResponses";
import AsyncSelect from "react-select/async";
import DropdownCheckbox from "./DropdownCheckbox";
import ExerciseInput from "./ExerciseInput";
import RepsInput from "./RepsInput";
import FileUploadZone, { ImageFile, VideoFile } from "./FileUploadZone";
import { HiVideoCamera, HiMusicNote } from "react-icons/hi";

enum MediaType {
  AUDIO = "AUDIO",
  VIDEO = "VIDEO",
}

const EditMediaModal = (props: any) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touchedFields, setTouchedFields] = useState<string[]>([]);
  const [, setActiveTab] = useState(0);
  const tabsRef = useRef<TabsRef>(null);

  const [video, setVideo] = useState({
    muscle_target: 0,
    workout_video: "",
    exercise_explained_video: "",
    trainer: 0,
    title: "",
    points: 0,
    reps: 0,
    video_duration: 0,
    brief: "",
    category: 0,
    published: false,
    video_thumbnail: "",
    video_thumbnail_type: "",
    workout_video_name: "",
    workout_video_type: "",
    explained_video_name: "",
    explained_video_type: "",
    image_name: "",
    image_type: "",
    exercise: "",
    equipment: [],
    media_type: MediaType.VIDEO,
  } as any);

  const fileTypes = ["image/jpg", "image/png", "image/jpeg"];

  const [image, setImage] = useState<ImageFile | null>(null);

  const [explainedVideo, setExplainedVideo] = useState<VideoFile | null>(null);

  const [workoutVideo, setWorkoutVideo] = useState<VideoFile | null>(null);

  const [exerciseTypeId, setExerciseType] = useState<string>();
  const [allMuscles] = useState<any>(props?.commonData?.muscles);
  const [selectedMuscles, setSelectedMuscles] = useState();
  const [allEquipment, setAllEquipment] = useState<Equipment[]>([]);
  const [selectedEquipmentForSelect, setSelectedEquipmentForSelect] = useState<
    any[]
  >([]);

  useEffect(() => {
    const fetchInitialEquipment = async () => {
      try {
        const equipmentData = await getAllEquipment();
        setAllEquipment(equipmentData);
      } catch (error) {
        console.error("Failed to fetch equipment", error);
        toast.error("Failed to load equipment list.");
      }
    };
    fetchInitialEquipment();
  }, []);

  useEffect(() => {
    if (props.commonData && video.exercise) {
      const exerciseDetails = props.commonData.exercises.find(
        (e: any) => e.id === video.exercise,
      );
      if (exerciseDetails) {
        setSelectedMuscles(exerciseDetails.muscles);
      }
    }
  }, [props.commonData, video.exercise]);

  useEffect(() => {
    if (allEquipment.length > 0 && video.equipment) {
      const equipmentIds = Array.isArray(video.equipment)
        ? video.equipment
        : [video.equipment].filter(Boolean);

      const selectedItems = allEquipment
        .filter((eq) => equipmentIds.includes(eq.id))
        .map((eq) => ({ value: eq.id, label: eq.name }));
      setSelectedEquipmentForSelect(selectedItems);
    } else {
      setSelectedEquipmentForSelect([]);
    }
  }, [video.equipment, allEquipment]);

  useEffect(() => {
    if (props.commonData && video.exercise) {
      const selectedExercise = props.commonData.exercises.find(
        (e: any) => e.id === video.exercise,
      );

      if (selectedExercise) {
        const muscles = selectedExercise.muscles || [];
        setSelectedMuscles(muscles);

        setVideo((prev: any) => {
          const updatedFields: any = {
            ...prev,
            muscles: muscles.reduce((acc: any, muscle: any) => {
              if (muscle?.id) acc.push(muscle.id);
              return acc;
            }, []),
          };

          if (!touchedFields.includes("brief_manual")) {
            updatedFields.brief = selectedExercise.description ?? "";
            markFieldAsTouched("brief");
          }
          if (!touchedFields.includes("rep_count_manual")) {
            updatedFields.rep_count = selectedExercise.repCount ?? "";
            markFieldAsTouched("rep_count");
          }
          if (!touchedFields.includes("set_count_manual")) {
            updatedFields.set_count = selectedExercise.setCount ?? "";
            markFieldAsTouched("set_count");
          }
          if (!touchedFields.includes("equipment_manual")) {
            updatedFields.equipment =
              selectedExercise.equipment?.map((eq: Equipment) => eq.id) || [];
            markFieldAsTouched("equipment");
          }

          return updatedFields;
        });
      }
    }
  }, [video.exercise, props.commonData]);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;

    if (!touchedFields.includes(name)) {
      setTouchedFields((prev) => [...prev, name]);
    }

    if (["brief", "rep_count", "set_count", "equipment"].includes(name)) {
      setTouchedFields((prev) =>
        prev.includes(`${name}_manual`) ? prev : [...prev, `${name}_manual`],
      );
    }

    setVideo((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "exercise" && props.commonData) {
      setTouchedFields((prev) =>
        prev.filter(
          (field) =>
            ![
              "brief_manual",
              "rep_count_manual",
              "set_count_manual",
              "equipment_manual",
            ].includes(field),
        ),
      );
    }
  };

  const handleEquipmentChange = (selectedOptions: any) => {
    const equipmentIds = selectedOptions
      ? selectedOptions.map((option: any) => option.value)
      : [];
    setVideo((prev: any) => ({
      ...prev,
      equipment: equipmentIds,
    }));
    if (!touchedFields.includes("equipment")) {
      setTouchedFields((prev) => [...prev, "equipment"]);
    }
    setTouchedFields((prev) =>
      prev.includes("equipment_manual") ? prev : [...prev, "equipment_manual"],
    );
    setSelectedEquipmentForSelect(selectedOptions || []);
  };

  const loadEquipmentOptions = async (inputValue: string) => {
    try {
      const result = await searchEquipment(inputValue);
      return result.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    } catch (error) {
      console.error("Error loading equipment options:", error);
      toast.error("Failed to search equipment.");
      return [];
    }
  };

  useEffect(() => {
    const {
      trainer,
      title,
      points,
      video_duration,
      brief,
      published,
      setCount,
      repCount,
      exercise,
      equipment,
      musicGenre,
      musicArtist,
      video_thumbnail_image,
      video_filename,
      video_url,
      exercise_explained_video_url,
      exercise_explained_video_filename,
      media_type,
    } = props.video;

    setVideo({
      trainer: trainer?.id || "",
      title: title,
      points: points,
      set_count: setCount,
      rep_count: repCount,
      video_duration: video_duration,
      muscles: exercise?.muscles?.map((e: any) => e?.id) || [],
      exercise: exercise?.id || "",
      equipment: equipment?.map((eq: any) => eq.id) || [],
      music_genre: musicGenre?.id || "",
      music_artist: musicArtist,
      brief: brief,
      published: published,
      video_thumbnail: true as any,
      video_thumbnail_type: "",
      workout_video_type: "",
      explained_video_type: "",
      image_name: "Img-03",
      image_type: "",
      media_type: media_type || MediaType.VIDEO,
    });

    if (props.commonData && exercise?.id) {
      const selectedExercise = props.commonData.exercises.find(
        (e: any) => e.id === exercise?.id,
      );
      const newTypeId = selectedExercise?.exerciseType.id;
      setExerciseType(newTypeId);

      if (selectedExercise) {
        const manualFields: string[] = [];

        if (brief !== selectedExercise.description) {
          manualFields.push("brief_manual");
        }

        if (repCount !== selectedExercise.repCount) {
          manualFields.push("rep_count_manual");
        }

        if (setCount !== selectedExercise.setCount) {
          manualFields.push("set_count_manual");
        }

        const exerciseEquipmentIds =
          selectedExercise.equipment?.map((eq: Equipment) => eq.id) || [];
        const currentEquipmentIds = Array.isArray(video.equipment)
          ? video.equipment
          : [video.equipment].filter(Boolean);

        if (
          JSON.stringify(currentEquipmentIds.sort()) !==
          JSON.stringify(exerciseEquipmentIds.sort())
        ) {
          manualFields.push("equipment_manual");
        }

        if (manualFields.length > 0) {
          setTouchedFields((prev) => [...prev, ...manualFields]);
        }
      }
    }

    if (video_thumbnail_image) {
      setImage({
        name: "thumbnail",
        dataUri: video_thumbnail_image,
        type: "image/png",
      });
    }

    if (video_url) {
      setWorkoutVideo({
        storageKey: video_url,
        name: video_filename,
        type: "video/mp4",
      });
    }

    if (exercise_explained_video_url) {
      setExplainedVideo({
        storageKey: exercise_explained_video_url,
        name: exercise_explained_video_filename,
        type: "video/mp4",
      });
    }
  }, [props.video, props.commonData]);

  const handleSubmit = async () => {
    let modifiedData: Record<string, any> = {};

    try {
      setIsSubmitting(true);

      modifiedData = touchedFields.reduce(
        (result, field) => {
          if (!field.endsWith("_manual")) {
            result[field] = video[field];
          }
          return result;
        },
        {} as Record<string, any>,
      );

      ["brief", "rep_count", "set_count", "equipment"].forEach((field) => {
        if (touchedFields.includes(`${field}_manual`)) {
          modifiedData[field] = video[field];
        }
      });

      if (touchedFields.includes("video_thumbnail")) {
        modifiedData["video_thumbnail"] = image?.dataUri;
        modifiedData["image_name"] = image?.name;
        modifiedData["image_type"] = image?.type;
      }

      if (touchedFields.includes("workout_video")) {
        modifiedData["workout_video"] = workoutVideo?.storageKey;
        modifiedData["workout_video_name"] = workoutVideo?.name;
        modifiedData["workout_video_type"] = workoutVideo?.type;
      }

      if (touchedFields.includes("explained_video")) {
        modifiedData["exercise_explained_video"] = explainedVideo?.storageKey;
        modifiedData["exercise_explained_video_name"] = explainedVideo?.name;
        modifiedData["exercise_explained_video_type"] = explainedVideo?.type;
      }

      const response = await updateVideo(modifiedData, props.video.id);

      if (response.data.error) {
        toast.error(response.data.message);
        Sentry.captureMessage("Error updating a video", {
          extra: {
            id: props.video.id,
            payload: modifiedData,
          },
        });
      } else {
        props.getVideos();
        toast.success("Media updated successfully!");
        props.onClose();
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      Sentry.captureMessage("Error updating a video", {
        extra: {
          id: props.video.id,
          payload: modifiedData,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const markFieldAsTouched = (field: string) => {
    setTouchedFields((prev) =>
      prev.includes(field) ? prev : [...prev, field],
    );
  };

  const trainerGender = useMemo(() => {
    if (props.commonData?.trainers) {
      const trainerId = parseInt(video.trainer);
      const currentTrainer = props.commonData.trainers.find(
        (e: any) => e.id === trainerId,
      );
      if (currentTrainer) {
        return currentTrainer.gender ? "Female" : "Male";
      }
    }
    return "Gender";
  }, [video, props.commonData]);

  const isSubmitDisabled = useMemo(() => {
    // For audio, only require: title, brief, workout_video, video_thumbnail, music_genre, music_artist
    if (video.media_type === MediaType.AUDIO) {
      const audioFieldValidation = [
        "title",
        "brief",
        "music_genre",
        "music_artist",
      ];

      const isEmptyField = audioFieldValidation.some((field: string) => {
        const fieldValue = (video as any)[field];
        if (Object.hasOwn(video, field)) {
          return (
            fieldValue === null ||
            fieldValue === undefined ||
            fieldValue.toString().length === 0
          );
        }
        return true;
      });

      return (
        isEmptyField ||
        isSubmitting ||
        !workoutVideo ||
        workoutVideo?.status === "uploading" ||
        !image
      );
    }

    // For video, use existing validation
    const selectedExerciseType = props.commonData?.exerciseTypes.find(
      (x: any) => x.id === exerciseTypeId,
    );
    const isInstructional = selectedExerciseType?.name === "Instructional";

    const fieldValidation = [
      "music_genre",
      "music_artist",
      "exercise",
      "video_duration",
      "title",
      "trainer",
    ];

    if (!isInstructional) {
      fieldValidation.push("equipment", "rep_count", "set_count", "points");
    }

    const isEmptyField = fieldValidation.some((field: string) => {
      const fieldValue = (video as any)[field];
      if (field === "equipment") {
        return !fieldValue || fieldValue.length === 0;
      }
      if (Object.hasOwn(video, field)) {
        return (
          fieldValue === null ||
          fieldValue === undefined ||
          fieldValue.toString().length === 0
        );
      }
      return true;
    });

    return (
      isEmptyField ||
      isSubmitting ||
      workoutVideo?.status === "uploading" ||
      explainedVideo?.status === "uploading"
    );
  }, [
    video,
    workoutVideo,
    explainedVideo,
    isSubmitting,
    exerciseTypeId,
    props.commonData,
    image,
  ]);

  useEffect(() => {
    if (workoutVideo?.duration) {
      handleChange({
        target: {
          name: "video_duration",
          value: workoutVideo.duration,
        },
      });
    }
  }, [workoutVideo]);

  // Set the active tab based on media type after video state is loaded
  useEffect(() => {
    const mediaType = props.video?.media_type;
    if (mediaType) {
      const tabIndex = mediaType === MediaType.AUDIO ? 1 : 0;
      setActiveTab(tabIndex);

      // Use a slight delay to ensure the Tabs component is fully rendered
      const timer = setTimeout(() => {
        tabsRef.current?.setActiveTab(tabIndex);
      }, 100);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [props.video?.media_type]);

  if (props?.commonData && video.title) {
    return (
      <Modal
        onClose={() => props.onClose()}
        show={true}
        size="3xl"
        position="top-center"
        root={document.body}
      >
        <ModalHeader className="border-b border-gray-200 p-6! dark:border-gray-700">
          <strong>Edit Media</strong>
        </ModalHeader>
        <ModalBody>
          <Tabs
            aria-label="Media type tabs"
            ref={tabsRef}
            onActiveTabChange={(tab) => setActiveTab(tab)}
          >
            <TabItem active title="Video" icon={HiVideoCamera}>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="firstName">Title</Label>
                  <div className="mt-1">
                    <TextInput
                      id="title"
                      name="title"
                      placeholder="Title"
                      onChange={handleChange}
                      defaultValue={video.title}
                    />
                  </div>
                </div>
                <div>
                  <Label>{"Brief (Optional)"}</Label>
                  <div className="mt-1">
                    <Textarea
                      id="brief"
                      name="brief"
                      placeholder="Brief"
                      onChange={handleChange}
                      value={video.brief}
                    />
                  </div>
                </div>

                <div>
                  <Label>Workout Video</Label>
                  <FileUploadZone
                    onVideoChange={(video) => {
                      setWorkoutVideo(video);
                      markFieldAsTouched("workout_video");
                    }}
                    acceptedTypes={["video/mp4"]}
                    defaultPreview={{
                      name: workoutVideo?.name,
                      url: workoutVideo?.storageKey,
                      type: "video/mp4",
                    }}
                    maxSize={1024 * 1024 * 1024}
                  />
                  {workoutVideo?.size && (
                    <div className="mt-3 text-[11px]">
                      <p>
                        Total Size: {workoutVideo?.size} MB | Status:{" "}
                        {workoutVideo?.status === "uploading"
                          ? `In Progress ${Math.round(workoutVideo.progress || 0)}%`
                          : "Ready"}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label>Video Thumbnail</Label>
                  <FileUploadZone
                    onImageChange={(data) => {
                      setImage(data);
                      markFieldAsTouched("video_thumbnail");
                    }}
                    acceptedTypes={fileTypes}
                    defaultPreview={{
                      name: image?.name || "",
                      url: image?.dataUri,
                      type: image?.type,
                    }}
                    maxDimensions={{ width: 2000, height: 2000 }}
                  />
                </div>

                <div>
                  <Label>Select Trainer</Label>
                  <div className="mt-1">
                    <Select onChange={handleChange} name="trainer">
                      <option disabled>Choose</option>
                      {props.commonData?.trainers?.map((x: any) => {
                        return (
                          <option
                            selected={x.id === video.trainer}
                            key={x.id}
                            value={x.id}
                          >
                            {x.title}
                          </option>
                        );
                      })}
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Trainer Gender</Label>
                  <div className="mt-1">
                    <TextInput
                      readOnly
                      disabled
                      id="trainer_gender"
                      name="trainer_gender"
                      placeholder="Type"
                      type="text"
                      value={trainerGender}
                    />
                  </div>
                </div>

                <DropdownCheckbox
                  muscles={allMuscles}
                  value={selectedMuscles}
                  onChange={handleChange}
                  disabled={true}
                />

                <div>
                  <Label>Video Duration</Label>
                  <div className="mt-1">
                    <TextInput
                      id="video_duration"
                      name="video_duration"
                      placeholder="Video Duration"
                      onChange={handleChange}
                      value={video.video_duration}
                    />
                  </div>
                </div>

                <div>
                  <Label>Exercise Type</Label>
                  <div className="mt-1">
                    <Select
                      onChange={(e) => setExerciseType(e.target.value)}
                      name="exerciseType"
                    >
                      <option selected disabled>
                        Choose
                      </option>
                      {props.commonData?.exerciseTypes.map((x: any) => {
                        return (
                          <option
                            key={x.id}
                            value={x.id}
                            selected={x.id === exerciseTypeId}
                          >
                            {x.name}
                          </option>
                        );
                      })}
                    </Select>
                  </div>
                </div>

                <ExerciseInput
                  handleChange={handleChange}
                  exerciseTypeId={exerciseTypeId}
                  commonData={props?.commonData}
                  selected={video.exercise}
                />

                <RepsInput
                  exerciseTypeId={exerciseTypeId}
                  commonData={props?.commonData}
                  handleChange={handleChange}
                  value={video.rep_count}
                />

                <div>
                  <Label>Sets</Label>
                  <div className="mt-1">
                    <TextInput
                      id="set_count"
                      name="set_count"
                      placeholder="Sets"
                      onChange={handleChange}
                      type="number"
                      value={video.set_count}
                    />
                  </div>
                </div>

                <div>
                  <Label>Points</Label>
                  <div className="mt-1">
                    <TextInput
                      id="points"
                      name="points"
                      placeholder="Points"
                      onChange={handleChange}
                      type="number"
                      value={video.points}
                    />
                  </div>
                </div>

                <div>
                  <Label>Equipment</Label>
                  <div className="mt-1">
                    <AsyncSelect
                      isMulti
                      cacheOptions
                      defaultOptions={allEquipment.map((eq) => ({
                        value: eq.id,
                        label: eq.name,
                      }))}
                      loadOptions={loadEquipmentOptions}
                      onChange={handleEquipmentChange}
                      value={selectedEquipmentForSelect}
                      placeholder="Search and select equipment..."
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                </div>

                <div>
                  <Label>Music Genre</Label>
                  <div className="mt-1">
                    <Select onChange={handleChange} name="music_genre">
                      <option selected disabled>
                        Choose
                      </option>
                      {props.commonData?.musicGenres.map((x: any) => {
                        return (
                          <option
                            key={x.id}
                            value={x.id}
                            selected={x.id === video.music_genre}
                          >
                            {x.name}
                          </option>
                        );
                      })}
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Music Artist</Label>
                  <div className="mt-1">
                    <TextInput
                      id="music_artist"
                      name="music_artist"
                      placeholder="Type"
                      type="string"
                      onChange={handleChange}
                      value={video.music_artist}
                    />
                  </div>
                </div>

                <div>
                  <Label>{"Workout Explaination Video (Optional)"}</Label>
                  <FileUploadZone
                    onVideoChange={(video) => {
                      setExplainedVideo(video);
                      markFieldAsTouched("explained_video");
                    }}
                    acceptedTypes={["video/mp4"]}
                    defaultPreview={
                      explainedVideo
                        ? {
                            name: explainedVideo?.name,
                            url: explainedVideo?.storageKey,
                            type: "video/mp4",
                          }
                        : undefined
                    }
                    maxSize={1024 * 1024 * 1024}
                  />
                  {explainedVideo?.size && (
                    <div className="mt-3 text-[11px]">
                      <p>
                        Total Size: {explainedVideo?.size} MB | Status:{" "}
                        {explainedVideo?.status === "uploading"
                          ? `In Progress ${Math.round(explainedVideo.progress || 0)}%`
                          : "Ready"}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-start"></div>
              </div>

              <div>
                <div className="mt-10">
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      name="published"
                      className="peer sr-only"
                      onChange={handleChange}
                      checked={video.published}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                      Published
                    </span>
                  </label>
                </div>
              </div>
            </TabItem>

            <TabItem title="Audio" icon={HiMusicNote}>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <div className="mt-1">
                    <TextInput
                      id="title"
                      name="title"
                      placeholder="Title"
                      onChange={handleChange}
                      value={video.title}
                    />
                  </div>
                </div>

                <div>
                  <Label>Brief</Label>
                  <div className="mt-1">
                    <Textarea
                      id="brief"
                      name="brief"
                      placeholder="Brief"
                      onChange={handleChange}
                      value={video.brief}
                    />
                  </div>
                </div>

                <div>
                  <Label>Audio File</Label>
                  <FileUploadZone
                    onVideoChange={(video) => {
                      setWorkoutVideo(video);
                      markFieldAsTouched("workout_video");
                    }}
                    acceptedTypes={["video/mp4", "audio/mpeg", "audio/mp3"]}
                    defaultPreview={{
                      name: workoutVideo?.name,
                      url: workoutVideo?.storageKey,
                      type: workoutVideo?.type || "audio/mpeg",
                    }}
                    maxSize={1024 * 1024 * 1024}
                  />
                  {workoutVideo?.size && (
                    <div className="mt-3 text-[11px]">
                      <p>
                        Total Size: {workoutVideo?.size} MB | Status:{" "}
                        {workoutVideo?.status === "uploading"
                          ? `In Progress ${Math.round(workoutVideo.progress || 0)}%`
                          : "Ready"}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label>Cover Image</Label>
                  <FileUploadZone
                    onImageChange={(data) => {
                      setImage(data);
                      markFieldAsTouched("video_thumbnail");
                    }}
                    acceptedTypes={fileTypes}
                    defaultPreview={{
                      name: image?.name || "",
                      url: image?.dataUri,
                      type: image?.type,
                    }}
                    maxDimensions={{ width: 2000, height: 2000 }}
                  />
                </div>

                <div>
                  <Label>Music Genre</Label>
                  <div className="mt-1">
                    <Select onChange={handleChange} name="music_genre">
                      <option selected disabled>
                        Choose
                      </option>
                      {props.commonData?.musicGenres.map((x: any) => {
                        return (
                          <option
                            key={x.id}
                            value={x.id}
                            selected={x.id === video.music_genre}
                          >
                            {x.name}
                          </option>
                        );
                      })}
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Music Artist</Label>
                  <div className="mt-1">
                    <TextInput
                      id="music_artist"
                      name="music_artist"
                      placeholder="Type"
                      type="string"
                      onChange={handleChange}
                      value={video.music_artist}
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="mt-10">
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      name="published"
                      className="peer sr-only"
                      onChange={handleChange}
                      checked={video.published}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                      Published
                    </span>
                  </label>
                </div>
              </div>
            </TabItem>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {isSubmitting ? "Updating..." : "Update"}
          </Button>
        </ModalFooter>
      </Modal>
    );
  } else {
    return (
      <Modal
        onClose={() => props.onClose()}
        show={true}
        size="3xl"
        position="top-center"
      >
        <ModalHeader className="border-b border-gray-200 p-6! dark:border-gray-700">
          <strong>Edit Media</strong>
        </ModalHeader>
        <ModalBody className="flex h-64 items-center justify-center">
          <div role="status">
            <svg
              aria-hidden="true"
              className="size-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {isSubmitting ? "Updating..." : "Update"}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
};

export default EditMediaModal;
